import * as React from 'react';
import UserChartNotificationSettingsInterface from '@app/Interface/UserChartNotificationSettingsInterface';
import User from '@app/Interface/UserInterface';
import ChartSettingCard from './ChartSettingCard';
import PageInterface from '@app/Interface/PageInterface';
import UserChartNotificationSettingsModel from '@app/Model/UserChartNotificationSettingsModel';
import ItemLoader from '../ItemLoader';

interface ChartSettingsProps {
    currentUser: User;
    notificationPageData: PageInterface;
    handleEditChartSetting: (show: boolean, setting: UserChartNotificationSettingsInterface, page: PageInterface | null) => void;
    chartPagesAreLoading: boolean;
    chartPages: PageInterface[];
    userChartSettings: UserChartNotificationSettingsInterface[];
}

const ChartSettings: React.FC<ChartSettingsProps> = ({ currentUser, notificationPageData, handleEditChartSetting, chartPagesAreLoading, chartPages, userChartSettings }) => {

    const [chartsAreLoading, setChartsAreLoading] = React.useState(true);

    // combined data from chartPages and userChartSettings
    // NOTE: if a user doesn't have a chart setting, then it is turned off by default. 
    const [chartSettings, setChartSettings] = React.useState<UserChartNotificationSettingsInterface[]>([]);

    // combine pages with existing user settings
    React.useEffect(() => {

        setChartsAreLoading(true);

        let settings: UserChartNotificationSettingsInterface[] = [];

        // loop through each chart page and build the chartSettings .
        chartPages.forEach(page => {

            const userSettingForChart = userChartSettings.find(setting => setting.chart === page.acf.chart_type);

            const recommendedSetting = new UserChartNotificationSettingsModel();

            const fromPageSetting = notificationPageData.acf.default_notification_thresholds?.find(threshold => threshold.analyte === page.acf.chart_type);

            if (fromPageSetting) {
                recommendedSetting.fpu = currentUser.currentFpuId;
                recommendedSetting.chart = fromPageSetting.analyte;
                recommendedSetting.threshold_range = fromPageSetting.threshold_range;
                recommendedSetting.threshold_value_min = fromPageSetting.threshold_value_min;
                recommendedSetting.threshold_value_max = fromPageSetting.threshold_value_max;
                recommendedSetting.threshold_display_value_min = fromPageSetting.threshold_value_min.toString();
                recommendedSetting.threshold_display_value_max = fromPageSetting.threshold_value_max.toString();
                recommendedSetting.isNew = false;
                recommendedSetting.active = false;
            }

            if (userSettingForChart) {
                userSettingForChart.isNew = false;
                userSettingForChart.threshold_display_value_min = userSettingForChart.threshold_value_min.toString();
                userSettingForChart.threshold_display_value_max = userSettingForChart.threshold_value_max.toString();
                settings.push(userSettingForChart);
            } else {
                // add a chart with default settings
                const defaultSetting = new UserChartNotificationSettingsModel();
                defaultSetting.active = false;
                defaultSetting.chart = page.acf.chart_type ?? "ash";
                defaultSetting.fpu = currentUser.currentFpuId;
                defaultSetting.threshold_range = recommendedSetting.threshold_range;
                defaultSetting.threshold_value_min = recommendedSetting.threshold_value_min;
                defaultSetting.threshold_value_max = recommendedSetting.threshold_value_max;
                defaultSetting.threshold_display_value_min = recommendedSetting.threshold_display_value_min;
                defaultSetting.threshold_display_value_max = recommendedSetting.threshold_display_value_max;
                defaultSetting.isNew = true;

                settings.push(defaultSetting);
            }

        });

        // sort alphabetically.
        // see: https://www.delftstack.com/howto/javascript/sort-array-based-on-some-property-javascript/
        settings = settings.sort((a, b) => {
            let sortValue = 0;
            if (a.chart.toLowerCase() < b.chart.toLowerCase()) {
                sortValue = -1;
            }
            if (a.chart.toLowerCase() > b.chart.toLowerCase()) {
                sortValue = 1;
            }
            return sortValue;
        });

        setChartSettings(settings);

        setChartsAreLoading(false);

    }, [chartPages, currentUser.currentFpuId, notificationPageData.acf.default_notification_thresholds, notificationPageData.title, userChartSettings]);

    if (chartPagesAreLoading || chartsAreLoading) {
        return (
            <ItemLoader />
        );
    }

    return (
        <>
            {chartSettings.length === 0
                ? <div>You don@apos;t have access to any charts for this FPU.</div>
                :
                <>
                    {chartSettings.map(setting => {
                        const chartSettingsPage = chartPages.find(page => page.acf.chart_type === setting.chart);
                        return <ChartSettingCard
                            key={setting.chart}
                            currentUser={currentUser}
                            setting={setting}
                            handleEditChartSetting={handleEditChartSetting}
                            page={chartSettingsPage ?? null}
                        />

                    })}
                </>
            }
        </>
    );

};

export default ChartSettings;